import React, { useEffect, useRef, useState } from "react";
import engdial from "../Components/Assets/engdial.png";
import needle from "../Components/Assets/needle.png";

// Read-only compass that mirrors the heading the customer's device streams
// over the Zoom command channel (see the "compass" message handler in
// ZoomAppointment). The master runs on desktop web with no magnetometer of
// its own, so this NEVER reads a sensor — it just renders whatever degree
// value it is handed.
//
// Physical luopan behaviour: the engraved direction dial (engdial.png)
// rotates so the sector the client is facing lands under the top of the
// needle, while the needle (needle.png) stays fixed pointing straight up,
// the same way the iOS widget does.

export default function RemoteCompass({ heading, size = 72, stale = false }) {
  const [expanded, setExpanded] = useState(false);

  // Accumulate turns so the dial never snaps the short way around when the
  // heading crosses 0/360 — mirrors the iOS CompassWidget's approach.
  const accRef = useRef(0);
  const lastRef = useRef(null);
  const [rot, setRot] = useState(0);

  const deg =
    heading == null ? null : Math.round(((Number(heading) % 360) + 360) % 360);

  useEffect(() => {
    if (deg == null) return;
    if (lastRef.current == null) {
      lastRef.current = deg;
      accRef.current = -deg;
      setRot(-deg);
      return;
    }
    let delta = deg - lastRef.current;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    lastRef.current = deg;
    accRef.current -= delta; // dial turns opposite to the heading
    setRot(accRef.current);
  }, [deg]);

  if (deg == null) return null;

  const renderDial = (px, withText) => (
    <div style={{ position: "relative", width: px, height: px }}>
      {/* Rotating engraved direction dial. */}
      <img
        src={engdial}
        alt=""
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          transform: `rotate(${rot}deg)`,
          transition: "transform 0.3s cubic-bezier(0.2,0.7,0.3,1)",
        }}
      />
      {/* Static needle — the heading is read where its top point meets the
          dial. Sits in the dial's open centre. */}
      <img
        src={needle}
        alt=""
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "46%",
          height: "46%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }}
      />
      {/* Degree readout, centered on the dial (over the needle's pivot) —
          mirrors the mobile compass (CompassPage.jsx), which shows the
          current heading's degree value alongside the direction. Shown at
          both the small badge size and the enlarged size. */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
          backgroundColor: "rgba(255,255,255,0.88)",
          borderRadius: 999,
          padding: withText ? "3px 10px" : "1px 6px",
          color: "#1a0f2e",
          fontWeight: 700,
          fontSize: px * (withText ? 0.11 : 0.16),
          lineHeight: 1,
          whiteSpace: "nowrap",
          boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
        }}
      >
        {deg}°
      </div>
      {withText && stale && (
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: -px * 0.13,
            textAlign: "center",
            color: "#deaf77",
            fontWeight: 700,
            fontSize: px * 0.09,
          }}
        >
          Signal lost
        </div>
      )}
    </div>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setExpanded(true)}
        title="Client's live compass heading — tap to enlarge"
        style={{
          width: size,
          height: size,
          padding: 4,
          borderRadius: "50%",
          border: "1px solid rgba(201,168,75,0.5)",
          backgroundColor: "rgba(10,3,30,0.85)",
          cursor: "pointer",
          opacity: stale ? 0.45 : 1,
          transition: "opacity 0.3s",
          boxSizing: "border-box",
        }}
      >
        {renderDial(size - 10, false)}
      </button>

      {expanded && (
        <div
          onClick={() => setExpanded(false)}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(10,3,30,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ position: "relative" }}
          >
            {renderDial(Math.min(window.innerWidth, window.innerHeight) * 0.78, true)}
          </div>
          <button
            onClick={() => setExpanded(false)}
            aria-label="Close"
            style={{
              position: "fixed",
              top: 24,
              right: 24,
              width: 44,
              height: 44,
              borderRadius: 22,
              border: "none",
              backgroundColor: "rgba(0,0,0,0.6)",
              color: "#fff",
              fontSize: 22,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
      )}
    </>
  );
}
